// frontend/src/components/VideoGeneration.js

import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Wizard from './Wizard';
import Scripting from './steps/Scripting';
import SceneEditing from './steps/SceneEditing';
import VideoReview from './steps/VideoReview';
import Audio from './steps/Audio';
import TextAndLogo from './steps/TextAndLogo';
import Finish from './steps/Finish';

const VideoGeneration = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [scriptingData, setScriptingData] = useState({
    // Add the new industry field to the initial state
    industry: '',
    account: '',
    businessEntity: '',
    description: '',
    targetMarket: '',
    videoLanguage: '',
    scenes: [
      {
        description: '',
        imageUrl: null,
        gcsImageUrl: null,
        length: '8s',
        videoLabeling: '',
        // scriptTemplate: 'VIDEO_LABELING'
        scriptTemplate: ''
      }
    ],
    generationModel: 'veo-3.0-generate-001',
    aspectRatio: '16:9'
  });

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    // fetch('/api/version')
    fetch(`${apiBaseUrl}/api/public/version`)
      .then(response => response.json())
      .then(data => console.log('API Version:', data.version))
      .catch(error => console.error('Error fetching version:', error));
  }, []);

  // Unused state variables can be removed for cleaner code
  // const [videoData, setVideoData] = useState(null);
  // const [sceneEditingData, setSceneEditingData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingStoryboard, setIsGeneratingStoryboard] = useState(false);
  const [isScriptingLoading, setIsScriptingLoading] = useState(true);
  const [storyboardData, setStoryboardData] = useState(null);
  const [mergedVideoPath, setMergedVideoPath] = useState(null);
  const [videos, setVideos] = useState([]);
  const sceneRef = useRef();

  const proceedToNextStep = (path, videos) => {
    setMergedVideoPath(path);
    setVideos(videos);
    setCurrentStep(currentStep + 1);
  };

  const nextStep = async () => {
    if (isGenerating) {
      alert('A generation task is still running. Please wait for it to complete before proceeding.');
      return;
    }
    if (currentStep === 2) {
      if (sceneRef.current) {
        sceneRef.current.handleNext();
      }
      return;
    }
    if (currentStep === 3) {
      if (mergedVideoPath) {
        proceedToNextStep(mergedVideoPath, videos);
      } else {
        alert('No video to proceed with.');
      }
      return;
    }
    if (currentStep === 1) {
      // Validate data
      const { industry, account, businessEntity, description, targetMarket, videoLanguage, scenes } = scriptingData;
      // Add validation for the new industry field
      if (!industry) {
        alert('Please select an industry.');
        return;
      }
      if (!account || !businessEntity || !description || !targetMarket || !videoLanguage) {
        alert('Please fill out all fields in the main form.');
        return;
      }
      if (scenes.length === 0) {
        alert('Please add at least one scene.');
        return;
      }
      for (const scene of scenes) {
        if (!scene.description) {
          alert('Please fill out the description for all scenes.');
          return;
        }
      }

      for (const [index, scene] of scriptingData.scenes.entries()) {
        if (scene.scriptTemplate !== 'VIDEO_LABELING' && scene.scriptTemplate === '') {
          alert(`Error in Scene #${index + 1}: Please select a valid script template.`);
          return;
        }
        if (scene.videoLabeling !== '' && scene.scriptTemplate !== 'VIDEO_LABELING') {
          alert(`Error in Scene #${index + 1}: A scene with a Video Labeling report selected has an incorrect mode. Please check your configuration.`);
          return;
        }
      }

      try {
        setIsGeneratingStoryboard(true);
        const payload = {
          shared_config: {
            // Add the industry field to the payload
            industry: scriptingData.industry,
            account: scriptingData.account,
            business_entity: scriptingData.businessEntity,
            description: scriptingData.description,
            target_market: scriptingData.targetMarket,
            video_language: scriptingData.videoLanguage,
            generation_model: scriptingData.generationModel,
            aspect_ratio: scriptingData.aspectRatio,
          },
          scene_configs: scriptingData.scenes.map(scene => ({
            scene_script_template_name: scene.scriptTemplate,
            scene_description: scene.description,
            video_length: scene.length,
            video_labeling_report_id: scene.videoLabeling,
            reference_image: scene.gcsImageUrl,
          })),
        };

        console.log("--- [LOG 1] PAYLOAD SENT to /api/storyboards ---");
        console.log(JSON.stringify(payload, null, 2));

        const response = await fetch(`${apiBaseUrl}/api/storyboards`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        if (response.ok) {
          const data = await response.json();
          console.log("--- [LOG 2] RAW DATA RECEIVED from /api/storyboards ---");
          console.log(JSON.stringify(data, null, 2));
          // Use optional chaining for safer access
          const storyboardArray = Array.isArray(data) ? data : data?.root;
          if (!Array.isArray(storyboardArray)) {
            console.error("Storyboard data is not a valid array:", storyboardArray);
            alert("The data received from the server was not in the expected format. Please check the browser console.");
            setIsGeneratingStoryboard(false);
            return;
          }
          const scenesForEditing = storyboardArray.map((scene, index) => {
            const originalScene = scriptingData.scenes[index];
            return {
              id: `${scene.metadata.requestId}-${index}`,
              scene: scene.data.prompt,
              description: originalScene ? originalScene.description : '',
              imageUrl: originalScene ? originalScene.imageUrl : null,
              gcsImageUrl: originalScene ? originalScene.gcsImageUrl : null,
              isSelected: true,
            };
          });
          console.log("--- [LOG 3] PROPS PASSED to SceneEditing component ---");
          console.log(JSON.stringify(scenesForEditing, null, 2));
          setStoryboardData(scenesForEditing);
          setCurrentStep(currentStep + 1);
          console.log('Storyboard scenesForEditing:', scenesForEditing);
        } else {
          const errorData = await response.json();
          // Handle cases where detail might not be an array
          const detail = errorData.detail;
          let errorMessages = 'An unknown error occurred.';
          if (Array.isArray(detail)) {
            errorMessages = detail.map(err => err.msg || JSON.stringify(err)).join('\n');
          } else if (typeof detail === 'string') {
            errorMessages = detail;
          }
          alert(`Error generating storyboard:\n${errorMessages}`);
          console.error('Error generating storyboard:', errorData);
        }
      } catch (error) {
        console.error('Error generating storyboard:', error);
        alert('An unexpected error occurred while generating the storyboard. Please check the console.');
      } finally {
        setIsGeneratingStoryboard(false);
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (isGenerating) {
      alert('A generation task is still running. Please wait for it to complete before proceeding.');
      return;
    }
    if (currentStep === 2) {
      if (window.confirm('Are you sure you want to go back? All your changes in this step will be lost.')) {
        setStoryboardData(null);
        setVideos([]);
        setCurrentStep(currentStep - 1);
      }
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <>
      {isGeneratingStoryboard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg flex flex-col items-center">
            <svg className="animate-spin h-10 w-10 text-blue-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-lg font-semibold">Generating Storyboard...</p>
            <p className="text-sm text-gray-600">This may take a moment.</p>
          </div>
        </div>
      )}
      <div className="p-8 pb-0">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">VeoMate Studio</h1>
      </div>
      <Wizard currentStep={currentStep} />
      <div className="px-4 py-2 flex justify-end border-b border-gray-200 dark:border-gray-700">
        {currentStep > 1 && (
          <button onClick={prevStep} className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-md mr-4 flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
        )}
        {currentStep < 6 && (
          <button onClick={nextStep} disabled={currentStep === 1 && isScriptingLoading} className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center disabled:bg-gray-400">
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        )}
      </div>
      <div className="p-4">
        { currentStep === 1 && <Scripting formData={scriptingData} setFormData={setScriptingData} setIsLoading={setIsScriptingLoading} /> }
        { currentStep === 2 && <SceneEditing ref={sceneRef} scenes={storyboardData} setIsGenerating={setIsGenerating} proceed={proceedToNextStep} videos={videos} generationModel={scriptingData.generationModel} aspectRatio={scriptingData.aspectRatio} /> }
        { currentStep === 3 && <VideoReview mergedVideoPath={mergedVideoPath} onNext={proceedToNextStep} /> }
        { currentStep === 4 && <Audio mergedVideoPath={mergedVideoPath} /> }
        { currentStep === 5 && <TextAndLogo mergedVideoPath={mergedVideoPath} /> }
        { currentStep === 6 && <Finish mergedVideoPath={mergedVideoPath} currentStep={currentStep} /> }
      </div>
    </>
  );
};

export default VideoGeneration;
